/**
 * Datas em PT-BR + cálculo de 6º dia útil.
 *
 * 6DU = 6º dia útil do mês — data-chave da liquidação CCEE no mercado de energia.
 * Considera finais de semana + feriados nacionais fixos.
 */

// Feriados nacionais fixos. Simplificado — móveis (carnaval, páscoa, Corpus)
// ficam pra v2; nos próximos meses só 21/4 e 1/5 coincidem, já cobertos.
const FERIADOS_NACIONAIS_MMDD = [
  "01-01", // Confraternização Universal
  "04-21", // Tiradentes
  "05-01", // Dia do Trabalho
  "09-07", // Independência
  "10-12", // Nossa Senhora Aparecida
  "11-02", // Finados
  "11-15", // Proclamação da República
  "12-25", // Natal
];

function mmdd(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${m}-${day}`;
}

export function isDiaUtil(d: Date): boolean {
  const dow = d.getDay(); // 0=dom, 6=sab
  if (dow === 0 || dow === 6) return false;
  if (FERIADOS_NACIONAIS_MMDD.includes(mmdd(d))) return false;
  return true;
}

/**
 * Retorna a data do 6º dia útil do mês informado.
 * mesRef: ano/mes; usa dia 1 pra iterar.
 */
export function sextoDiaUtilDoMes(ano: number, mes0: number): Date {
  const d = new Date(ano, mes0, 1);
  let count = 0;
  while (count < 6) {
    if (isDiaUtil(d)) count += 1;
    if (count < 6) d.setDate(d.getDate() + 1);
  }
  return d;
}

/**
 * Heurística OFFLINE do próximo 6º dia útil — mês corrente se hoje <= 6DU do
 * mês; mês seguinte caso contrário.
 *
 * NÃO conhece feriados móveis (Corpus Christi, Sexta Santa, Carnaval) — só os
 * fixos de `FERIADOS_NACIONAIS_MMDD` — então diverge do calendário CCEE oficial
 * em meses com feriado móvel (bugs #156/#157/#158/#51: 1 dia útil de off ⇒
 * fetch backend com data errada ⇒ card no mês errado / empty state).
 *
 * Fix #51 — HARD-DEPRECATE: esta função NÃO é mais exportada sob o nome
 * amigável `calcularProximo6DU`, justamente pra impedir uso acidental em UI
 * (5ª reincidência). A ÚNICA superfície pública é o alias _-prefixado
 * `_calcularProximo6duHeuristica`, importado só pelos 2 usos conscientes de
 * fallback: o caminho /catch do `useProximo6du` (API offline) e os cálculos
 * sintéticos de `mockData.ts`. Pra o próximo 6du oficial em UI, SEMPRE usar o
 * hook `useProximo6du` (calendário CCEE). NÃO reexportar sob nome amigável.
 */
function _proximo6duHeuristica(hoje: Date = new Date()): Date {
  const esteMes = sextoDiaUtilDoMes(hoje.getFullYear(), hoje.getMonth());
  if (hoje <= esteMes) return esteMes;
  return sextoDiaUtilDoMes(hoje.getFullYear(), hoje.getMonth() + 1);
}

/** Alias explícito da heurística OFFLINE — único nome exportado (#51). Usar
 * apenas quando intencionalmente NÃO se quer consultar o calendário CCEE
 * oficial (fallback do `useProximo6du` + cálculos sintéticos em mockData.ts). */
export const _calcularProximo6duHeuristica = _proximo6duHeuristica;

export function diasUteisAte(alvo: Date, hoje: Date = new Date()): number {
  // Conta dias úteis entre hoje (exclusivo) e alvo (inclusivo).
  let count = 0;
  const cursor = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate());
  while (cursor < alvo) {
    cursor.setDate(cursor.getDate() + 1);
    if (isDiaUtil(cursor)) count += 1;
  }
  return count;
}

const NOMES_MES = [
  "janeiro",
  "fevereiro",
  "março",
  "abril",
  "maio",
  "junho",
  "julho",
  "agosto",
  "setembro",
  "outubro",
  "novembro",
  "dezembro",
];

export function formatarDataLongaBr(d: Date): string {
  return `${d.getDate()} de ${NOMES_MES[d.getMonth()]} de ${d.getFullYear()}`;
}

export function formatarDataCurtaBr(d: Date): string {
  const day = String(d.getDate()).padStart(2, "0");
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${day}/${m}/${d.getFullYear()}`;
}

export function ymdIso(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/** Parse de timestamp do BACKEND (#25a F-25a-1): a casa grava datetimes UTC
 * NAIVE (datetime.now(UTC).replace(tzinfo=None)) e serializa SEM "Z" — o
 * construtor Date() interpretaria como hora LOCAL e a UI mostraria a hora
 * UTC rotulada como local (+3h no BRT). String sem offset → trata como UTC
 * (append "Z"); com offset/Z explícito → respeita. Formate SEMPRE em local
 * a partir do Date resultante. */
export function parseUtcNaive(iso: string): Date {
  const temOffset = /(?:Z|[+-]\d{2}:?\d{2})$/.test(iso.trim());
  return new Date(temOffset ? iso : `${iso}Z`);
}
